const initialized = new WeakMap();

export function once(element, key) {
	let keys = initialized.get(element);
	if (!keys) {
		keys = new Set();
		initialized.set(element, keys);
	}
	if (keys.has(key)) {
		return false;
	}
	keys.add(key);
	return true;
}

export function debounce(callback, delay = 200) {
	let timeout;
	return (...args) => {
		window.clearTimeout(timeout);
		timeout = window.setTimeout(() => callback(...args), delay);
	};
}

export function getConfig() {
	const node = document.getElementById('frontend-config');
	if (!node) {
		return {};
	}
	try {
		return JSON.parse(node.textContent || '{}');
	} catch (error) {
		console.error('Invalid frontend configuration.', error);
		return {};
	}
}

export function setBusy(element, busy) {
	if (!element) {
		return;
	}
	element.classList.toggle('is-loading', busy);
	if (busy) {
		element.setAttribute('aria-busy', 'true');
		if (element.matches('a')) {
			element.setAttribute('aria-disabled', 'true');
		}
		return;
	}
	element.removeAttribute('aria-busy');
	if (element.matches('a')) {
		element.removeAttribute('aria-disabled');
	}
}

export function initNaja(onUpdate) {
	if (!once(document.documentElement, 'frontend-naja')) {
		return;
	}
	window.naja.addEventListener('complete', () => onUpdate(document));
}

function initFooterAccordion(root) {
	const footer = root.matches?.('#footer') ? root : root.querySelector?.('#footer');
	if (!footer || !once(footer, 'footer-accordion')) {
		return;
	}

	const sections = [...footer.querySelectorAll('[data-footer-toggle]')]
		.map((toggle) => ({
			toggle,
			content: document.getElementById(toggle.getAttribute('aria-controls')),
		}))
		.filter(({content}) => content);
	const media = window.matchMedia('(max-width: 767px)');

	const setExpanded = (section, expanded) => {
		section.toggle.setAttribute('aria-expanded', String(expanded));
		section.content.hidden = !expanded;
		section.content.style.display = expanded ? 'block' : 'none';
		const icon = section.toggle.querySelector('i');
		icon?.classList.toggle('bi-caret-right-fill', !expanded);
		icon?.classList.toggle('bi-caret-down-fill', expanded);
	};

	const sync = () => {
		const mobile = media.matches;
		const modeChanged = footer.dataset.footerAccordionMode !== (mobile ? 'mobile' : 'desktop');
		footer.dataset.footerAccordionMode = mobile ? 'mobile' : 'desktop';
		footer.classList.toggle('accordion', mobile);

		sections.forEach((section) => {
			if (mobile) {
				section.toggle.setAttribute('role', 'button');
				section.toggle.setAttribute('tabindex', '0');
				setExpanded(section, modeChanged ? false : section.toggle.getAttribute('aria-expanded') === 'true');
				return;
			}
			section.toggle.removeAttribute('role');
			section.toggle.removeAttribute('tabindex');
			setExpanded(section, true);
		});
	};

	const toggleSection = (toggle) => {
		if (!media.matches) {
			return;
		}
		const section = sections.find((candidate) => candidate.toggle === toggle);
		if (section) {
			setExpanded(section, toggle.getAttribute('aria-expanded') !== 'true');
		}
	};

	sections.forEach(({toggle}) => {
		toggle.addEventListener('click', () => toggleSection(toggle));
		toggle.addEventListener('keydown', (event) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				toggleSection(toggle);
			}
		});
	});
	media.addEventListener('change', sync);
	sync();
}

function initShowMore(root) {
	root.querySelectorAll('.show-more[data-show-more-label]').forEach((content, index) => {
		if (!once(content, 'show-more')) {
			return;
		}
		const previewHeight = content.dataset.type === 'titulka' ? 200 : 100;
		if (content.scrollHeight <= previewHeight * 1.4) {
			return;
		}

		content.id ||= `show-more-${index + 1}`;
		content.style.setProperty('--show-more-height', `${previewHeight}px`);
		content.classList.add('show-more--collapsed');

		const label = content.dataset.showMoreLabel;
		const toggle = document.createElement('button');
		toggle.type = 'button';
		toggle.className = 'show-more__toggle';
		toggle.setAttribute('aria-controls', content.id);
		toggle.setAttribute('aria-expanded', 'false');
		toggle.setAttribute('aria-label', label);
		toggle.title = label;
		toggle.innerHTML = '<i class="bi bi-chevron-down" aria-hidden="true"></i>';
		content.insertAdjacentElement('afterend', toggle);

		toggle.addEventListener('click', () => {
			content.classList.remove('show-more--collapsed');
			content.classList.add('show-more--expanded');
			toggle.setAttribute('aria-expanded', 'true');
			toggle.remove();
		});
	});
}

export function init(root = document) {
	if (once(document.documentElement, 'loading-actions')) {
		document.addEventListener('click', (event) => {
			if (event.target.closest?.('.loading-action.is-loading')) {
				event.preventDefault();
				event.stopImmediatePropagation();
			}
		}, true);
	}
	root.querySelectorAll('img[data-src]').forEach((image) => {
		if (!image.getAttribute('src')) {
			image.src = image.dataset.src;
		}
		image.loading = 'lazy';
		image.removeAttribute('data-src');
	});
	const jsOn = root.querySelector('#jsOn');
	const jsOff = root.querySelector('#jsOff');
	if (jsOn) {
		jsOn.hidden = false;
	}
	if (jsOff) {
		jsOff.hidden = true;
	}
	initFooterAccordion(root);
	initShowMore(root);
}
